import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { IBilgePumpInfo } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { InfoGroupCard, type InfoGroupEntry } from '@/components/infoGroupCard/InfoGroupCard.tsx';
import {
  DeviceInfoOverview,
  deviceInfoOverviewGridColumns,
} from '@/components/informationModal/DeviceInfoOverview.tsx';
import type { DeviceSummary } from '@/types';
import { responsive } from '@/utils';

type BilgePumpInfoFieldsProps = {
  bilgePumpInfo: IBilgePumpInfo;
};

export const BilgePumpInfoFields = ({ bilgePumpInfo }: BilgePumpInfoFieldsProps) => {
  const { t } = useTranslation(['common', 'bargeControl']);

  const {
    fields: { controller },
    activateDieselDetection,
    bilgePumpIndex,
    dieselDetected,
    hasDieselDetection,
    leakageDetected,
    startManual,
  } = bilgePumpInfo;

  const overviewExtraEntries = useMemo((): InfoGroupEntry[] => {
    return [
      { id: 'bilgePumpIndex', label: t('bargeControl:bilgePump.index'), value: bilgePumpIndex },
    ];
  }, [bilgePumpIndex, t]);

  const operationEntries = useMemo(
    (): InfoGroupEntry[] => [
      {
        id: 'pumpMode',
        label: t('bargeControl:bilgePump.pumpMode'),
        value: startManual ? t('manual') : t('auto'),
      },
      {
        id: 'waterLeakage',
        label: t('bargeControl:alarm.bilgePump.waterLeakage'),
        value: leakageDetected ? t('bargeControl:status.alarm') : t('bargeControl:status.ok'),
      },
      { id: 'controller', label: t('controller'), value: controller },
    ],
    [controller, leakageDetected, startManual, t]
  );

  const dieselDetectionEntries = useMemo((): InfoGroupEntry[] => {
    const statusEntry: InfoGroupEntry = {
      id: 'dieselDetectionStatus',
      label: t('status'),
      value: activateDieselDetection ? t('on') : t('off'),
    };
    if (!activateDieselDetection) return [statusEntry];
    return [
      statusEntry,
      {
        id: 'dieselLeakage',
        label: t('bargeControl:alarm.bilgePump.dieselLeakage'),
        value: dieselDetected ? t('bargeControl:status.alarm') : t('bargeControl:status.ok'),
      },
    ];
  }, [activateDieselDetection, dieselDetected, t]);

  return (
    <FlexAligner direction='column' gap={responsive.spacing['3xlarge']}>
      <DeviceInfoOverview
        device={bilgePumpInfo as DeviceSummary}
        extraEntries={overviewExtraEntries}
      />
      <InfoGroupCard entries={operationEntries} columns={deviceInfoOverviewGridColumns} />
      {hasDieselDetection && (
        <InfoGroupCard
          header={{ title: t('bargeControl:dieselDetection') }}
          entries={dieselDetectionEntries}
          columns={deviceInfoOverviewGridColumns}
        />
      )}
    </FlexAligner>
  );
};
